import { UserType } from "../recoil/userAtom";
import { DecisionResult } from "./refundPolicyUtils";
import { isWithinDaysFromNow } from "./timeUtils";

// 결제 14일이 지나도 플랜 업그레이드를 열어주는 유저 (임시). 결제일 기준만 면제하고 나머지 조건은 그대로다.
const PLAN_UPGRADE_ALWAYS_ALLOWED_USER_IDS = [155031];

// userId 는 타입은 number 지만 런타임에 string 이라 Number() 로 변환 후 비교
function isPlanUpgradeAlwaysAllowed(userId?: number | string): boolean {
  return PLAN_UPGRADE_ALWAYS_ALLOWED_USER_IDS.includes(Number(userId));
}

export type PlanUpgradeDecisionKey =
  | "BENEFIT"
  | "MASTER"
  | "NOT_SUBSCRIBING"
  | "NOT_FIRST"
  | "PERIOD_OVER"
  | "ALREADY_UPGRADED"
  | "OPEN";

/**
 * Decide the result for the "플랜 업그레이드하기 >" link.
 * Mirrors the visibility conditions of PaymentHistoryPage's isPlanUpgradeButtonShown,
 * but resolves to either an info modal (조건 미충족) or navigation (key === "OPEN").
 */
export function decidePlanUpgrade(
  user?: UserType
): DecisionResult<PlanUpgradeDecisionKey> {
  const plan = user?.plan;
  const isSubscribing = !!user?.subscription?.isSubscribing;
  const isFirstSubscription = !!user?.subscription?.isFirstSubscription;
  const hasBeenPlanUpgraded = !!user?.subscription?.hasBeenPlanUpgraded;
  const latestPaidAt = user?.subscription?.latestPaidAt;

  // 혜택(코치 교육·인플루언서·체험 등) 이용자는 플랜 변경 대상이 아니다.
  if (user?.benefit) {
    return {
      key: "BENEFIT",
      title: "플랜 변경이 어려워요",
      subtitle: "현재 이용 중인 혜택은 플랜 변경 대상이 아니에요.",
    } as const;
  } else if (plan === "master") {
    return {
      key: "MASTER",
      title: "업그레이드할 플랜이 없어요",
      subtitle: "이미 마스터 플랜을 이용 중이에요.",
    } as const;
  } else if (!isSubscribing) {
    return {
      key: "NOT_SUBSCRIBING",
      title: "현재 이용 중이 아니에요",
      subtitle: "결제 상태를 확인 후 다시 시도해주세요.",
    } as const;
  } else if (!isFirstSubscription) {
    return {
      key: "NOT_FIRST",
      title: "최초 이용 중이 아니에요",
      subtitle: "플랜 변경에 대한 규정을 확인하신 후 다시 시도해주세요.",
    } as const;
  } else if (
    !isPlanUpgradeAlwaysAllowed(user?.userId) &&
    (!latestPaidAt || !isWithinDaysFromNow(latestPaidAt, 14))
  ) {
    return {
      key: "PERIOD_OVER",
      title: "플랜 변경 기간이 지났어요",
      subtitle: "결제일로부터 14일 이내에만 플랜 변경이 가능해요.",
    } as const;
  } else if (hasBeenPlanUpgraded) {
    return {
      key: "ALREADY_UPGRADED",
      title: "이미 플랜 변경을 했어요",
      subtitle: "결제 이후 최초 1회에 한해서만 플랜 변경이 가능해요.",
    } as const;
  }

  return { key: "OPEN", title: "", subtitle: "" } as const;
}

/**
 * 공통 플랜 업그레이드 가능 여부 (혜택 / master / 구독중 / 첫 결제 / 14일 이내 / 미변경).
 * pricingModel·blackout·활동지 완료 등 사이트별 조건은 호출부에서 별도로 AND 한다.
 */
export function canUpgradePlan(user?: UserType): boolean {
  return decidePlanUpgrade(user).key === "OPEN";
}

export type PlanDowngradeDecisionKey =
  | "STARTER"
  | "NOT_SUBSCRIBING"
  | "NOT_FIRST"
  | "PERIOD_OVER"
  | "ALREADY_CHANGED"
  | "CHANNEL_TALK";

/**
 * Decide the result for the "플랜 다운그레이드하기 >" link.
 * Mirrors decidePlanUpgrade, but the first branch checks for the starter plan
 * and the final branch is an info modal (no navigation).
 */
export function decidePlanDowngrade(
  user?: UserType
): DecisionResult<PlanDowngradeDecisionKey> {
  const plan = user?.plan;
  const isSubscribing = !!user?.subscription?.isSubscribing;
  const isFirstSubscription = !!user?.subscription?.isFirstSubscription;
  const hasBeenPlanUpgraded = !!user?.subscription?.hasBeenPlanUpgraded;
  const latestPaidAt = user?.subscription?.latestPaidAt;

  if (plan === "starter") {
    return {
      key: "STARTER",
      title: "다운그레이드할 플랜이 없어요",
      subtitle: "이미 스타터 플랜을 이용 중이에요.",
    } as const;
  } else if (!isSubscribing) {
    return {
      key: "NOT_SUBSCRIBING",
      title: "현재 이용 중이 아니에요",
      subtitle: "결제 상태를 확인 후 다시 시도해주세요.",
    } as const;
  } else if (!isFirstSubscription) {
    return {
      key: "NOT_FIRST",
      title: "최초 이용 중이 아니에요",
      subtitle: "플랜 변경에 대한 규정을 확인하신 후 다시 시도해주세요.",
    } as const;
  } else if (!latestPaidAt || !isWithinDaysFromNow(latestPaidAt, 14)) {
    return {
      key: "PERIOD_OVER",
      title: "플랜 변경 기간이 지났어요",
      subtitle: "결제일로부터 14일 이내에만 플랜 변경이 가능해요.",
    } as const;
  } else if (hasBeenPlanUpgraded) {
    return {
      key: "ALREADY_CHANGED",
      title: "이미 플랜 변경을 했어요",
      subtitle: "결제 이후 최초 1회에 한해서만 플랜 변경이 가능해요.",
    } as const;
  }

  return {
    key: "CHANNEL_TALK",
    title: "고객센터로 문의해주세요",
    subtitle: "플랜 다운그레이드 시 담당 코치가 변경될 수 있습니다.",
  } as const;
}
